import { SITE, TRACK_ENDPOINT } from "./constants";
import { normalizePath } from "./normalizePath";

export type AnalyticsEventName =
  | "pageview"
  | "estimator_step"
  | "estimator_submitted"
  | "calculator_run"
  | "signup"
  | "login"
  | "booking_requested"
  | "portal_quote_submitted"
  | "js_error"
  | "graphql_error";

export type AnalyticsProps = Record<string, string | number | boolean>;

// Captured once on load. Campaign params only appear on the landing URL, and
// document.referrer is our own site after the first client-side navigation, so
// reading either later would lose the attribution.
const entry = (() => {
  if (typeof window === "undefined") {
    return { referrer: "", source: "", medium: "", campaign: "" };
  }

  const params = new URLSearchParams(window.location.search);

  return {
    referrer: document.referrer,
    source: params.get("utm_source") ?? "",
    medium: params.get("utm_medium") ?? "",
    campaign: params.get("utm_campaign") ?? "",
  };
})();

/**
 * Fire-and-forget event send.
 *
 * sendBeacon survives the document unloading, which a plain fetch does not —
 * the common case is an event fired as the user navigates away. The body goes
 * as text/plain because that is a CORS-simple content type, so events never pay
 * for a preflight round trip.
 *
 * Nothing here throws: analytics must never break a page.
 *
 * Deliberately does not check navigator.doNotTrack. DNT asks not to be profiled
 * across visits, and there is no profile here to opt out of: no cookie, no
 * device id, and the IP is discarded once the daily hash is derived. Chrome and
 * Firefox set the flag widely enough that honouring it silently loses a large
 * share of real traffic for no privacy gain. Revisit this the moment any
 * persistent identifier is introduced — and note that Global Privacy Control
 * (Sec-GPC), unlike DNT, does carry legal weight in some US states.
 */
export const track = (name: AnalyticsEventName, props?: AnalyticsProps): void => {
  if (typeof window === "undefined") return;

  try {
    const body = JSON.stringify({
      name,
      site: SITE,
      path: normalizePath(window.location.pathname),
      referrer: entry.referrer,
      utm: {
        source: entry.source,
        medium: entry.medium,
        campaign: entry.campaign,
      },
      props,
    });

    if (navigator.sendBeacon?.(TRACK_ENDPOINT, body)) return;

    // Older Safari refuses beacons over a certain size and returns false.
    void fetch(TRACK_ENDPOINT, {
      method: "POST",
      body,
      headers: { "Content-Type": "text/plain" },
      credentials: "include",
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // Serialization or a blocked endpoint — neither is worth surfacing.
  }
};

export const trackPageview = (): void => track("pageview");
